/**
 * A "venue" is the physical setting the whole app is dressed in — menus and the
 * game table alike. Think of the room you're playing in, not a color scheme:
 * the wood of the rail, the fabric of the playing surface, the paper of the
 * score pad. Each venue defines its materials twice:
 *
 *  - as CSS custom properties, applied via `data-venue` on <html> (styles.css)
 *  - as numeric colors for the Pixi table renderer (the `pixi` block here)
 *
 * Two venues are available from the start; the rest unlock through levels and
 * achievements (see theme/unlocks.ts). Every venue also owns its own generative
 * ambience in audio/sound.ts — adding one here means adding a soundscape there.
 *
 * A PREMIUM venue additionally carries `material` and `air`, which is what makes
 * it a room rather than a recolor. See docs/PREMIUM_VENUES.md.
 */
export type VenueId =
  | "coastal"
  | "alpine"
  | "library"
  | "pavilion"
  | "observatory"
  | "velvet"
  | "olympian"
  | "biolume"
  | "porch"
  | "hallowed"
  | "meadhall"
  | "singularity";

export interface VenuePixi {
  /** Main playing-surface fabric (linen / wool baize). */
  surface: number;
  /** Light pooling in the middle of the table, as if lit from above. */
  surfaceLight: number;
  /** Shaded fabric near the rail. */
  surfaceShade: number;
  /** Stitched + inlaid marking lines on the surface (stack outlines, perimeter). */
  inlay: number;
  /** Wood rail around the table. */
  rail: number;
  railLight: number;
  railDark: number;
  /** Resting (not-their-turn) player name labels. */
  label: number;
}

/**
 * How the table is BUILT, as named treatments rather than more numbers.
 *
 * Every field defaults to what the free venues already draw, so adding a
 * treatment here cannot change the six that ship — a venue opts in by naming it.
 * `drawSurface` switches on these, and all of it is baked into the cached
 * surface texture, so a premium table costs exactly what a free one does per
 * frame however much detail it carries.
 */
export interface VenueMaterial {
  /** Rail: turned wood, fluted stone, knapped volcanic glass, woven cane, a wrought-iron graveyard railing, or oak carved with knotwork. */
  rail?: "wood" | "stone" | "glass" | "wicker" | "iron" | "carved" | "void";
  /** The line around the playing area: a stitched seam, a Greek-key meander, a chain of photophores, a trail of paw prints, a creeping pumpkin vine, or a runic serpent biting its tail. */
  seam?: "stitch" | "meander" | "photophore" | "pawprint" | "vine" | "serpent" | "photon";
  /** Under each stack: a pressed cloth well, an inlaid bronze medallion, a ring of light, a little braided rug, a headstone, or a round shield. */
  marker?: "stitch" | "medallion" | "anemone" | "rug" | "gravestone" | "shield" | "accretion";
  /** Marble veining through a stone rail, or the fracture lines in a glass one. Ignored by wood, wicker and iron. */
  vein?: number;
  /**
   * THE VENUE'S ONE BRIGHT LINE — the rail bead, the seam, the marker rings.
   *
   * Named for the gilding it was introduced to draw, and deliberately not
   * renamed when a venue lit by nothing but its own animals started using it for
   * cold light instead. What the field means is the treatment it feeds, not the
   * metal: every place it is read wants "the color this table's edges catch",
   * and a second field for the same job would let a venue's edges disagree with
   * each other.
   */
  gild?: number;
}

/**
 * The LIVE layer: light that moves, drawn above the baked surface and below the
 * cards.
 *
 * This is the half that reads as expensive. A static light pool is seen as a
 * texture and stops being noticed in the first round; one that wanders is seen
 * as a room. Cheap — a handful of sprites on very long tweens.
 */
export interface VenueAir {
  /** How far the light pool wanders from center, as a fraction of the shorter side. */
  drift: number;
  /** Seconds for one full wander. Long: this must never read as an animation. */
  driftSec: number;
  /** A specular sweep crossing the table, as if light moved outside the room. */
  sweep?: { color: number; sec: number };
  /**
   * Motes drifting in the light pool.
   *
   * They rise, because dust in a lit room rises. `fall` reverses that for a
   * venue where the particles are marine snow, which does the opposite and is
   * the single most recognisable thing about deep water.
   */
  motes?: { color: number; count: number; fall?: boolean };
  /**
   * REACTION: the room answers a big moment with light, then forgets it.
   *
   * The third layer in docs/PREMIUM_VENUES.md and the cheapest one — a single
   * sprite lit for under a second by events the renderer already plays. It is
   * opt-in per venue rather than automatic for anything with `air`, because a
   * room that answers has to have a reason to: this belongs to a table lit
   * entirely by living light, and would read as a flashbulb on a marble one.
   */
  flare?: { color: number };
  /**
   * REACTION, of a different kind: something small runs across the table.
   *
   * A trail of paw prints appears step by step under the cards and fades, as if
   * an animal had just dashed through. It is the Sun Porch's answer to `flare`,
   * and it is a separate field rather than a flare in a warmer color because a
   * room answers in its own terms — a flash of light is what water full of
   * living things does, and a porch full of pets does this.
   */
  scamper?: { color: number };
  /**
   * Fog: a few long banks of mist crawling across the cloth, under the cards.
   *
   * Not motes and not a sweep: both of those are LIGHT, and fog is the opposite
   * — it takes contrast away rather than adding a highlight, so it is drawn as
   * big soft wisps at low alpha that drift sideways, each bank on its own period
   * so they thicken and thin as they pass through one another.
   */
  fog?: { color: number; banks: number };
  /**
   * Jack-o'-lanterns carved into the table's corners, and the candle behind
   * each face: a warm pool that gutters on its own clock. The pumpkins are baked
   * with the rail; only the candlelight is live.
   */
  lanterns?: { color: number };
  /**
   * REACTION: a flurry of bats bursts across the table on a Five Out.
   *
   * The one reaction drawn ABOVE the cards, and deliberately so: paw prints and
   * light belong to the table, but bats are in the air over it, and a flurry
   * that passed under the cards would read as shadows on the cloth. It is over
   * in about two seconds, inside the banner's hold, so it never sits over a card
   * anybody is trying to read.
   */
  bats?: { color: number };
  /**
   * A hearth off the near edge of the table: a broad warm glow rising from the
   * bottom of the cloth that breathes and gutters like a fire, slower and wider
   * than a candle. Live, because it moves; the room has no lamp but this.
   */
  hearth?: { color: number };
  /**
   * REACTION: the runes kindle. On a Five Out a light runs round the runic
   * band in the seam, rune by rune, then the whole band glows and dies down.
   * Placed from the same `runeSpots` the baked runes are, so a glow can never
   * sit beside its rune. Under the cards, like every reaction but the bats.
   */
  kindle?: { color: number };
  /**
   * A field of stars across the cloth, each twinkling on its own clock, a few
   * of them bright enough to throw diffraction spikes. Not motes: motes drift
   * in a lamp's light, and these are a long way off and never move.
   */
  stars?: { color: number; count: number };
  /**
   * Nebula: a few huge, very faint clouds of colour turning slowly over the
   * cloth. Additive like every light here, and far fainter than the pool.
   */
  nebula?: { colors: number[] };
  /**
   * REACTION: a black hole opens in the middle of the table on a Five Out.
   * Starlight streaks inward and spirals down, the accretion ring spins up and
   * flares, then it collapses and a gravitational wave rolls out across the
   * cloth. Under the cards, like every reaction but the bats.
   */
  singularity?: { ring: number; wave: number };
}

/** The sets of pictures an Epic room's seam can be embroidered with (pixi/stitchMotifs.ts). */
export type Embroidery = "garden" | "heavens";

export interface Venue {
  id: VenueId;
  name: string;
  tagline: string;
  pixi: VenuePixi;
  /** Premium venues only — see docs/PREMIUM_VENUES.md. */
  material?: VenueMaterial;
  /** Premium venues, and the Epic rooms (theme/rarity.ts), which get the light without the treatments. */
  air?: VenueAir;
  /**
   * Epic rooms only: pictures sewn into the plain stitched seam in place of
   * every other stitch (pixi/stitchMotifs.ts). A set's room replaces the seam
   * outright with a `material.seam`; an Epic room keeps the seam and works on it.
   */
  embroidery?: Embroidery;
}

export const VENUES: Venue[] = [
  {
    id: "coastal",
    name: "Coastal Terrace",
    tagline: "Sun-bleached linen, driftwood & brass",
    pixi: {
      surface: 0x74a18e,
      surfaceLight: 0x86b29f,
      surfaceShade: 0x5c8a77,
      inlay: 0xf1e8d2,
      rail: 0xb3a184,
      railLight: 0xd8c9ab,
      railDark: 0x8a765a,
      label: 0xf3f2e7,
    },
  },
  {
    id: "alpine",
    name: "Alpine Lodge",
    tagline: "Walnut, wool baize & firelight",
    pixi: {
      surface: 0x3d5a4e,
      surfaceLight: 0x48695b,
      surfaceShade: 0x2e463c,
      inlay: 0xd9b36c,
      rail: 0x503b28,
      railLight: 0x76573b,
      railDark: 0x332518,
      label: 0xd8cdb6,
    },
  },
  {
    id: "library",
    name: "The Reading Room",
    tagline: "Oxblood leather, oak shelves & lamplight",
    pixi: {
      surface: 0x6d3f3a,
      surfaceLight: 0x814b45,
      surfaceShade: 0x532e2b,
      inlay: 0xe8cf9a,
      rail: 0x4a3320,
      railLight: 0x6d4c30,
      railDark: 0x2c1e12,
      label: 0xf0e2c4,
    },
  },
  {
    id: "pavilion",
    name: "Garden Pavilion",
    tagline: "Cane, moss velvet & morning birdsong",
    pixi: {
      surface: 0x6f8f5e,
      surfaceLight: 0x82a26e,
      surfaceShade: 0x577349,
      inlay: 0xfaf3dc,
      rail: 0xc0a97e,
      railLight: 0xe0cda3,
      railDark: 0x93805c,
      label: 0xfbf6e6,
    },
    /*
     * An Epic room (theme/rarity.ts): live light, but no treatments of its own
     * and nothing that answers a big moment - those stay a set's. Morning sun
     * through the cane: a warm pool that wanders like light through leaves,
     * pollen rising in it, and now and then the sun coming out from a cloud.
     */
    air: {
      drift: 0.09,
      driftSec: 40,
      sweep: { color: 0xfff1c2, sec: 44 },
      motes: { color: 0xfff4c8, count: 14 },
    },
    embroidery: "garden",
  },
  {
    id: "observatory",
    name: "Night Observatory",
    tagline: "Brass instruments, cold glass & starlight",
    pixi: {
      surface: 0x2b3552,
      surfaceLight: 0x374465,
      surfaceShade: 0x1e2640,
      inlay: 0x9fc3e8,
      rail: 0x3f4358,
      railLight: 0x5e6379,
      railDark: 0x22253a,
      label: 0xd6e2f5,
    },
    /*
     * An Epic room, like the Pavilion: starlight in the air over the cloth, and
     * moonlight crossing the table slowly as if the dome turned. Cold, and
     * sparse, so the Constellations events (drawn in their own starlight) are
     * still the brightest thing in here.
     */
    air: {
      drift: 0.06,
      driftSec: 58,
      sweep: { color: 0xc9dcff, sec: 64 },
      motes: { color: 0xdbe8ff, count: 18 },
    },
    embroidery: "heavens",
  },
  {
    id: "velvet",
    name: "The Velvet Room",
    tagline: "Plum velvet, smoked mirrors & late nights",
    pixi: {
      surface: 0x4a2f52,
      surfaceLight: 0x5b3b64,
      surfaceShade: 0x37223d,
      inlay: 0xe9c46a,
      rail: 0x2f2033,
      railLight: 0x4b3550,
      railDark: 0x1d1320,
      label: 0xf1e3f2,
    },
  },
  /**
   * Mount Olympus — the venue half of the Olympian Pantheon set (theme/sets.ts).
   *
   * Its materials are not invented here: they are read off the deck's own spec
   * in assets/decks/olympian-pantheon.deck.json, whose card back names "deep
   * indigo, bronze, old gold, dark purple" and whose ornament vocabulary is
   * marble, gilded lightning, olive and wrought iron. A venue art-directed
   * separately from the deck it ships with drifts from it, and the drift is the
   * first thing a player sees.
   *
   * The surface is wine-dark rather than marble, and the marble is in the rail.
   * The painted faces all have pale, open centers (the deck's bible reserves the
   * middle third for the numeral), so a pale table would put light on light and
   * lose every card — the gate in docs/PREMIUM_VENUES.md is specifically about
   * not doing that. Dark cloth, bright frame, gold only in thin lines.
   */
  {
    id: "olympian",
    name: "Mount Olympus",
    tagline: "Wine-dark cloth, weathered marble & old gold",
    pixi: {
      surface: 0x241c3e,
      surfaceLight: 0x342a53,
      surfaceShade: 0x171130,
      inlay: 0xd4a94e,
      rail: 0xb9b0a0,
      railLight: 0xe2dbc9,
      railDark: 0x7b7367,
      label: 0xe8e0f2,
    },
    material: {
      rail: "stone",
      seam: "meander",
      marker: "medallion",
      vein: 0x9b93a8,
      gild: 0xd4a94e,
    },
    air: {
      drift: 0.07,
      driftSec: 46,
      sweep: { color: 0xf3e2b0, sec: 37 },
      motes: { color: 0xf0e2c0, count: 16 },
    },
  },
  /**
   * The Midnight Zone — the venue half of the Biolume Deep set (theme/sets.ts).
   *
   * Art-directed off the deck's own spec, the same way Mount Olympus is:
   * assets/decks/biolume-deep.deck.json asks for "a smooth, matte, near-black
   * surface with subtle, scattered glowing particles", lit "entirely from the
   * bioluminescent subjects".
   *
   * THIS TABLE IS THE EXACT OPPOSITE OF THE OLYMPIAN ONE, and for the same
   * reason. Olympus' cards have pale open centers, so it needed dark cloth and a
   * bright frame. These cards are near-black in the middle with a glowing cyan
   * filigree around the edge — the card frames ITSELF, brightly — so a pale rail
   * would be a second bright frame around the first one, and the eye would go to
   * the furniture. Hence a rail darker than the cloth, no bright material
   * anywhere, and one cold luminous line: the room is unlit and the cards are
   * the light in it, which is what the deck is called.
   *
   * The cloth is still LIGHTER than the cards' black centers, and that margin is
   * the whole gate here. Matching the deck's near-black exactly would be truer
   * to the abyss and would dissolve every card silhouette into the table.
   */
  {
    id: "biolume",
    name: "The Midnight Zone",
    tagline: "Black water, volcanic glass & living light",
    pixi: {
      surface: 0x0d2029,
      surfaceLight: 0x16333e,
      surfaceShade: 0x061218,
      inlay: 0x53e0d8,
      rail: 0x081016,
      railLight: 0x3d6a78,
      railDark: 0x02060a,
      label: 0xbfeef2,
    },
    material: {
      rail: "glass",
      seam: "photophore",
      marker: "anemone",
      vein: 0x3a6b78,
      gild: 0x53e0d8,
    },
    air: {
      // Water moves more than air does, and further: the pool wanders twice as
      // far as Olympus' and takes longer doing it, so the light reads as
      // something drifting overhead rather than a lamp with a loose fitting.
      drift: 0.13,
      driftSec: 62,
      sweep: { color: 0x9ff2ea, sec: 58 },
      motes: { color: 0xa8f0ff, count: 26, fall: true },
      flare: { color: 0x7fecff },
    },
  },
  /**
   * The Sun Porch — the venue half of the Animal Antics set (theme/sets.ts).
   *
   * Read off the deck the same way the other two premium rooms are:
   * assets/decks/animal-antics-deck.deck.json paints puppies, kittens, a gecko
   * and bear cubs in warm gouache, framed in carved honey-gold filigree, with a
   * card back of "deep brown, gold, forest green, terracotta" whose own hue is a
   * deep sage (#3E5641). So the cloth is that sage, the rail is a honeyed cane
   * basket, and everything ornamental on the table is something a pet left
   * behind: paw prints round the edge, little braided rugs under the piles.
   *
   * WHERE THIS SITS ON THE GATE. These cards are neither of the two cases the
   * other rooms were built for. Their centers are MID-tone — a cocoa rug, a
   * terracotta floor, dusk blue, moss — and their frames are pale gold. A pale
   * table would lose the frames and a near-black one would make the centers
   * look muddy; a mid-dark sage sits under both, and is the complementary hue to
   * the gold, so a card's edge separates from the cloth on hue as well as on
   * lightness. The honey rail is lighter than the cloth, but its weave keeps its
   * contrast in small marks rather than in one bright band, which is how it
   * stays quieter than the cards.
   *
   * The light is afternoon sun rather than a lamp: warm, drifting, with dust
   * rising through it — the kitten on the clubs card is batting at exactly that.
   */
  {
    id: "porch",
    name: "The Sun Porch",
    tagline: "Wicker, braided rugs & afternoon sun",
    pixi: {
      surface: 0x46593f,
      surfaceLight: 0x586c4b,
      surfaceShade: 0x33422d,
      inlay: 0xf0dcae,
      rail: 0xb08650,
      railLight: 0xe0c08a,
      railDark: 0x6e4f2b,
      label: 0xf7eedb,
    },
    material: {
      rail: "wicker",
      seam: "pawprint",
      marker: "rug",
      gild: 0xf3ddab,
    },
    air: {
      drift: 0.09,
      driftSec: 52,
      // Sun through a window, crossing the table every forty-odd seconds as
      // though a cloud had moved.
      sweep: { color: 0xffe2a4, sec: 43 },
      // Dust in a sunbeam: rises, catches the light, fades.
      motes: { color: 0xfff0cc, count: 20 },
      scamper: { color: 0xf3ddab },
    },
  },
  /**
   * Hallowed Ground — the venue half of the Midnight Macabre set (theme/sets.ts).
   *
   * Read off the deck like the other premium rooms:
   * assets/decks/midnight-macabre.deck.json paints a witch, a ghost bride, a
   * zombie and a vampire in oil on cracked stone, framed in black thorned iron,
   * with a card back of a wrought-iron bat on weathered charcoal. So the rail is
   * a graveyard railing, the stack markers are headstones, and a pumpkin vine
   * creeps round the cloth to the jack-o'-lanterns at the corners.
   *
   * WHERE THIS SITS ON THE GATE. These cards are the darkest-EDGED in the game:
   * a grey open center inside near-black thorns. A black table would swallow
   * every silhouette, and a grey one would put grey cards on grey. So the cloth
   * separates on HUE — a dusky midnight violet, the color of the sky over a
   * churchyard an hour after sunset — and sits a clear step lighter than the
   * thorns. The iron rail is darker than the cloth, the Midnight Zone's answer
   * for the same reason: a bright frame round cards that frame themselves in
   * black would be a second border the eye settles on.
   *
   * The one bright color is candle orange, and it is kept to the pumpkins'
   * faces and a thread in the vine. That is the "festive" half of the brief, and
   * it is rationed because orange is the loudest color on a violet table.
   */
  {
    id: "hallowed",
    name: "Hallowed Ground",
    tagline: "Gravestones, jack-o'-lanterns & creeping fog",
    pixi: {
      surface: 0x3a3350,
      surfaceLight: 0x4b4366,
      surfaceShade: 0x29233b,
      inlay: 0xc9a6e6,
      rail: 0x2a2830,
      railLight: 0x6b6874,
      railDark: 0x111016,
      label: 0xece4f6,
    },
    material: {
      rail: "iron",
      seam: "vine",
      marker: "gravestone",
      gild: 0xf29a3a,
    },
    air: {
      drift: 0.08,
      driftSec: 58,
      // Moonlight, not a lamp: cold, and a long way off.
      sweep: { color: 0xb9c4ff, sec: 61 },
      fog: { color: 0xcfc6e8, banks: 5 },
      lanterns: { color: 0xff9a2e },
      bats: { color: 0x0e0b14 },
    },
  },
  /**
   * The Mead Hall — the venue half of the Norse Realms set (theme/sets.ts).
   *
   * Read off the deck like every premium room: assets/decks/norse-realms.deck.json
   * paints gods and giants on "deeply carved runestone, weathered oak, and
   * hammered silver inlay", in knotwork, "lit by a hearth fire", and its back is
   * Jörmungandr biting its own tail inside the branches of the World Tree, in
   * "weathered oak, verdigris, dark silver". So the rail is oak carved with an
   * interlace, the line round the cloth is the World Serpent as a runestone's
   * inscription band, the stack markers are the round shields hung along a
   * longhouse wall, and the only lamp in the room is the fire.
   *
   * WHERE THIS SITS ON THE GATE. These cards have near-black centers inside
   * frames of mid-grey carved stone and warm brown oak — darker-centered than
   * Olympus, but framed in a MID tone rather than the Midnight Zone's glow or
   * Hallowed Ground's black. A grey or brown table would put stone on stone and
   * oak on oak, and a black one would swallow the centers. So the cloth
   * separates on HUE: the verdigris of the deck's own back, a deep green-teal a
   * clear step lighter than the card centers, which neither the grey frames nor
   * the brown ones share. The carved rail is darker than the cloth, so the
   * frames stay the lightest structure on the table, and its one bright line is
   * a thin bead of hammered silver — the deck's inlay metal, never gold.
   */
  {
    id: "meadhall",
    name: "The Mead Hall",
    tagline: "Carved oak, runestones & hearthfire",
    pixi: {
      surface: 0x22433d,
      surfaceLight: 0x2d554d,
      surfaceShade: 0x172f2a,
      inlay: 0xb7c9c0,
      rail: 0x33261b,
      railLight: 0x6a5039,
      railDark: 0x150e09,
      label: 0xece6d8,
    },
    material: {
      rail: "carved",
      seam: "serpent",
      marker: "shield",
      gild: 0xc8d0d2,
    },
    air: {
      drift: 0.06,
      driftSec: 49,
      // The northern lights, seen through the smoke-hole: cold green crossing
      // a warm room, rarely, and a long way off.
      sweep: { color: 0x8fe3bd, sec: 73 },
      // Embers off the fire, rising.
      motes: { color: 0xffa04a, count: 18 },
      hearth: { color: 0xff8a3a },
      kindle: { color: 0xffc46e },
    },
  },
  /**
   * The Singularity — the venue half of the Fractal Aether set (theme/sets.ts),
   * and the room Zenith, the campaign's last boss, is played in.
   *
   * Read off the deck like every premium room: assets/decks/fractal-aether.deck.json
   * paints its suits in "long-exposure light painting and glowing ink in water",
   * on a back of gold and indigo fractals meeting in the middle — and its Void
   * Weavers spade already carries a small black hole, accretion disk and all, in
   * the corner. So the table is that black hole's edge: void cloth, a rail of
   * black glass with starlight lensed into streaks along it, a photon ring for
   * the one bright line, and a little black hole under every pile.
   *
   * WHERE THIS SITS ON THE GATE. The Midnight Zone's answer, for the Midnight
   * Zone's reason: these cards have smoky near-black centers inside frames of
   * vivid neon fractal light — gold, magenta, teal, violet — so they frame
   * THEMSELVES, brightly, and a bright rail would be a second frame round the
   * first. The rail is the darkest thing on the table, and the cloth is a deep
   * saturated indigo a clear step lighter than the cards' centers, so a card's
   * dark middle never dissolves into the void it is lying on.
   *
   * The one warm light is the photon ring's gold — the deck back's gold, and the
   * color the real ones were photographed in — kept to hairlines.
   */
  {
    id: "singularity",
    name: "The Singularity",
    tagline: "Void, bent starlight & a black hole's edge",
    pixi: {
      surface: 0x1b1940,
      surfaceLight: 0x27245a,
      surfaceShade: 0x0f0d28,
      inlay: 0xa8b4ff,
      rail: 0x07070f,
      railLight: 0x3c3d6e,
      railDark: 0x010104,
      label: 0xdfe0ff,
    },
    material: {
      rail: "void",
      seam: "photon",
      marker: "accretion",
      vein: 0xb9c2ff,
      gild: 0xffc98a,
    },
    air: {
      drift: 0.08,
      driftSec: 66,
      // Light bent round the hole: a pale shimmer crossing the table, rarely.
      sweep: { color: 0xc9c4ff, sec: 69 },
      stars: { color: 0xe8ecff, count: 90 },
      // The deck's own colors, as gas: violet, teal and the back's gold.
      nebula: { colors: [0x7a3fa0, 0x2f7f9a, 0x9a6a2a] },
      singularity: { ring: 0xffaa4a, wave: 0xa8b4ff },
    },
  },
];

/** The two venues every player starts with. The rest unlock (see theme/unlocks.ts). */
export const STARTER_VENUE_IDS: VenueId[] = ["coastal", "alpine"];

const VENUE_KEY = "fiveout:venue";

/**
 * The venue being VISITED, which is not always the one the player chose.
 *
 * An online table belongs to its host — their cards, their room — and a guest
 * plays in it whether or not they own it. That was already true of the felt,
 * because `GameCanvas` takes the host's venue as a prop, and it was true of
 * nothing else: the ambience and the HTML chrome both went on reading the
 * player's own saved venue, so a guest sat at the host's Midnight Zone table
 * hearing the Coastal Terrace with a Coastal Terrace HUD around it.
 *
 * Held in a module variable rather than in storage precisely BECAUSE it must not
 * outlive the visit. Writing the host's venue through `setSelectedVenueId` would
 * be the obvious shortcut and would quietly overwrite the player's own
 * preference — they would leave a game and find their room had been redecorated
 * by somebody else.
 */
let visitingVenueId: VenueId | null = null;

export function venueById(id: string): Venue | undefined {
  return VENUES.find((v) => v.id === id);
}

/** The player's OWN venue, as saved. Unaffected by whose table they are sitting at. */
export function getSelectedVenue(): Venue {
  const id = localStorage.getItem(VENUE_KEY);
  return venueById(id ?? "") ?? VENUES[0];
}

/** The venue actually on screen and in the speakers right now — the host's, in an online game. */
export function getActiveVenue(): Venue {
  return (visitingVenueId ? venueById(visitingVenueId) : undefined) ?? getSelectedVenue();
}

/**
 * Dresses the app in somebody else's venue for as long as the player is in it.
 * `null` hands them back their own.
 *
 * Idempotent, because it is called from a React effect that re-runs whenever the
 * host's published venue changes — and `sound.setVenue` tears the ambience down
 * and builds it again, which would be an audible restart on every snapshot that
 * happened to touch that field.
 */
/** The venue being visited, if any - so something that borrows the room can hand back whatever it found. */
export function getVisitingVenueId(): VenueId | null {
  return visitingVenueId;
}

export function setVisitingVenueId(id: VenueId | null): void {
  if (visitingVenueId === id) return;
  visitingVenueId = id;
  applyVenue();
}

export function setSelectedVenueId(id: VenueId): void {
  localStorage.setItem(VENUE_KEY, id);
  applyVenue();
}

/**
 * Puts the active venue on the screen and in the speakers: the `data-venue`
 * stamp the CSS variables hang off, the felt color, and the ambience.
 *
 * The ambience moved in here from `setSelectedVenueId` when visiting became
 * possible. There were then two ways to change rooms and only one of them
 * carried the sound, which is exactly the bug being fixed — so now there is one
 * function that means "this is the room I am in" and everything that follows
 * from that happens in it.
 */
export function applyVenue(): void {
  const venue = getActiveVenue();
  document.documentElement.dataset.venue = venue.id;
  // The ambience follows the venue; lazy import avoids an audio dependency at
  // module load. `setVenue` no-ops when the room has not actually changed.
  void import("../audio/sound").then(({ sound }) => sound.setVenue(venue.id));
  // The playing surface, published to CSS.
  //
  // Every other material is declared twice on purpose — once for CSS, once for
  // Pixi — but the felt is not a menu color, it is the table itself, and the
  // only HTML that wants it wants it to MATCH the table exactly (the rule flash
  // is outlined in it, so the wordmark reads as cut out of the cloth). A second
  // copy in styles.css would be six values free to drift from the six the table
  // is actually painted with, so it is derived from the one that draws it.
  publishFelt(venue.pixi.surface);
  // The rest of the cloth, for the home title's felt letters (CardTitle): the
  // light pooled in the middle, the shade near the rail, and the thread the
  // seams are stitched in. Derived here for the same reason as `--felt`.
  const root = document.documentElement.style;
  root.setProperty("--felt-light", hexOf(venue.pixi.surfaceLight));
  root.setProperty("--felt-shade", hexOf(venue.pixi.surfaceShade));
  root.setProperty("--felt-thread", hexOf(venue.pixi.inlay));
}

/**
 * Puts the felt color in CSS as `--felt`. The table calls it too: a painted
 * deck leans the cloth towards the suits on the table, and the one piece of
 * HTML that reads this wants it to match the cloth actually on screen.
 */
export function publishFelt(color: number): void {
  document.documentElement.style.setProperty("--felt", hexOf(color));
}

/** A Pixi 0xRRGGBB color as the `#rrggbb` CSS wants. */
function hexOf(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}
